import { Injectable } from '@angular/core';
import {
  HttpResponse,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { LoaderService } from './modules/loader/loader.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthTokenService } from './core/auth/auth-token.service';

@Injectable()
export class LoaderInterceptorService implements HttpInterceptor {
    private requests: HttpRequest<any>[] = [];


    constructor(
        private loaderService: LoaderService,
        private router: Router,
        private _matSnackBar: MatSnackBar,
        private authTokenService: AuthTokenService
    ) { }

    removeRequest(req: HttpRequest<any>) {
        const i = this.requests.indexOf(req);
        if (i >= 0) {
            this.requests.splice(i, 1);
        }

        const hideLoader = req.params.get('Hide-Loader');

        if (hideLoader != null && hideLoader != undefined && hideLoader.toString() == "true") {
            this.loaderService.isLoading.next(false);
        }
        else {
            this.loaderService.isLoading.next(this.requests.length > 0);
        }
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        this.requests.push(req);
        if (req.body?.headers?.hideLoader != undefined && req.body.headers.hideLoader) {
            req.body.headers.hideLoader = false;
            var dummyReq = req;
            this.removeRequest(req);
            return next.handle(dummyReq);

        }
        else {
            this.loaderService.isLoading.next(true);
            return new Observable(observer => {
                const subscription = next.handle(req)
                    .pipe(
                        catchError((err) => {
                            if (err.status == 401 && !this.isAuthRefreshRequest(req) && !req.headers.has('X-Retry-After-Refresh')) {
                                // force=true: server rejected access token even if client clock still thinks it is valid
                                return this.authTokenService.refreshAccessToken(true).pipe(
                                    switchMap((refreshed) => {
                                        if (!refreshed) {
                                            // Only hard-logout when refresh token is gone / definitively invalid
                                            if (!this.authTokenService.hasValidRefreshToken()) {
                                                this.handleUnauthorized();
                                            }
                                            return throwError(() => err);
                                        }

                                        const token = this.authTokenService.accessToken;
                                        const retryReq = req.clone({
                                            setHeaders: {
                                                Authorization: `${token}`,
                                                'X-Retry-After-Refresh': 'true'
                                            }
                                        });
                                        return next.handle(retryReq);
                                    })
                                );
                            }

                            if (err.status == 401) {
                                if (!this.authTokenService.hasValidRefreshToken()) {
                                    this.handleUnauthorized();
                                }
                            }
                            else if (err.status == 404) {
                                console.log(err);
                                if (err.error.subscription != undefined) {
                                    this._matSnackBar.open(err.error.subscription.errors[0].errorMessage, 'OK', {
                                        verticalPosition: "top",
                                        duration: 5 * 1000
                                    });
                                    this.router.navigateByUrl('/subscription/subscription-details');
                                }
                            }
                            else if (err.status == 422) {
                                console.log(err);
                                this._matSnackBar.open("Please select a company", 'OK', {
                                    verticalPosition: "top",
                                    duration: 5 * 1000
                                });
                                window.location.href = "/select-company";
                            }

                            return throwError(() => err);
                        })
                    )
                    .subscribe({
                        next: (event) => {
                            if (event instanceof HttpResponse) {
                                this.removeRequest(req);
                                observer.next(event);
                            }
                        },
                        error: (err) => {
                            this.removeRequest(req);
                            observer.error(err);
                        },
                        complete: () => {
                            this.removeRequest(req);
                            observer.complete();
                        }
                    });
                // remove request from queue when cancelled
                return () => {
                    this.removeRequest(req);
                    subscription.unsubscribe();
                };
            });
        }
    }

    private isAuthRefreshRequest(req: HttpRequest<any>): boolean {
        return req.url.toLowerCase().includes('api/auth/refresh');
    }

    private handleUnauthorized(): void {
        this._matSnackBar.open("Unauthorized Request", 'OK', {
            verticalPosition: "top",
            duration: 5 * 1000
        });
        this.authTokenService.clearTokens();
        window.location.href = "/login";
    }
}
